import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * The design uses native `<select>` elements, which cannot be styled past the
 * trigger, so this is the Radix rebuild of that exact look: white, the 1.5px
 * border, an 11px radius, semibold Inter at 0.9rem, and 38px of right padding
 * for a chevron parked at `right:13px` in the muted ink.
 *
 * The popover has no counterpart in `Visual - Demo.dc.html` (the browser drew
 * it), so it is assembled from the design's own vocabulary: card shadow, input
 * radius, 1.5px border, and the brand tint for the highlighted/selected row.
 *
 * It sits *above* the whole modal ladder in `dialog.tsx` — 60 standard, 65
 * stacked, 70 scan flow — rather than below it. A select opened inside a drawer
 * used to render behind the drawer: both are portalled to `document.body`, the
 * popover was `z-50` and the scrim `z-60`, so the list opened underneath an
 * opaque panel and the control looked simply dead to a mouse. Keyboard still
 * worked, which is the signature of this bug if it ever comes back. A popover
 * belongs above whatever surface opened it, so this is the top of the ladder,
 * not a step on it.
 */

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

type SelectTriggerProps = ComponentProps<typeof SelectPrimitive.Trigger>;

export function SelectTrigger({
  className,
  children,
  ...props
}: SelectTriggerProps) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        "relative flex w-full cursor-pointer items-center justify-between",
        "rounded-input border-[1.5px] border-border bg-surface",
        "py-3 pr-[38px] pl-4 font-sans text-[0.9rem] font-semibold text-ink",
        "data-[placeholder]:font-normal data-[placeholder]:text-faint",
        "disabled:pointer-events-none disabled:opacity-50",
        "[&>span]:truncate",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown
          size={16}
          className="pointer-events-none absolute top-1/2 right-[13px] -translate-y-1/2 text-muted"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

type SelectContentProps = ComponentProps<typeof SelectPrimitive.Content>;

export function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        className={cn(
          "relative z-80 max-h-96 min-w-[8rem] overflow-hidden",
          "rounded-input border-[1.5px] border-border bg-surface shadow-card",
          "data-[state=open]:animate-rise",
          position === "popper" &&
            "w-full min-w-[var(--radix-select-trigger-width)]",
          className,
        )}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

type SelectItemProps = ComponentProps<typeof SelectPrimitive.Item>;

export function SelectItem({ className, children, ...props }: SelectItemProps) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex cursor-pointer items-center gap-2 select-none",
        "rounded-control py-2 pr-8 pl-3 font-sans text-[0.9rem] text-ink",
        "outline-none data-[highlighted]:bg-brand-tint-2",
        "data-[highlighted]:text-brand",
        "data-[state=checked]:bg-brand-tint data-[state=checked]:text-brand",
        "data-[state=checked]:font-semibold",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute right-2 flex items-center">
        <Check size={15} />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

type SelectLabelProps = ComponentProps<typeof SelectPrimitive.Label>;

/** The Oswald eyebrow the design uses above every grouped list. */
export function SelectLabel({ className, ...props }: SelectLabelProps) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        "px-3 py-2 font-display text-[0.7rem] font-semibold uppercase",
        "tracking-[0.1em] text-muted",
        className,
      )}
      {...props}
    />
  );
}

type SelectSeparatorProps = ComponentProps<typeof SelectPrimitive.Separator>;

export function SelectSeparator({ className, ...props }: SelectSeparatorProps) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("-mx-1 my-1 h-px bg-border-inner", className)}
      {...props}
    />
  );
}

type SelectScrollButtonProps = ComponentProps<
  typeof SelectPrimitive.ScrollUpButton
>;

export function SelectScrollUpButton({
  className,
  ...props
}: SelectScrollButtonProps) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up"
      className={cn(
        "flex cursor-default items-center justify-center py-1 text-muted",
        className,
      )}
      {...props}
    >
      <ChevronUp size={15} />
    </SelectPrimitive.ScrollUpButton>
  );
}

export function SelectScrollDownButton({
  className,
  ...props
}: SelectScrollButtonProps) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down"
      className={cn(
        "flex cursor-default items-center justify-center py-1 text-muted",
        className,
      )}
      {...props}
    >
      <ChevronDown size={15} />
    </SelectPrimitive.ScrollDownButton>
  );
}

export { Select, SelectGroup, SelectValue };
export type { SelectContentProps, SelectItemProps, SelectTriggerProps };
